import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { getItinerariesListAPI } from "../../../../../api/itinerary/getSaveItinerariesList";
import { ROUTES } from "../../../../../constant";
import { APP_CONFIG } from "../../../../../constant/common";
import { SELECTED_CHAT_DATA } from "../../../../../constant/dummyData";
import { resolveBoolFlag } from "../../../../../utils/appConfig/resolveBoolFlag";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../../../../constant/google-analytics/GAEvents";
import { useCurrentTripContext } from "../../../../../context/currentTripContext/currentTripContext";
import { iCurrentTripContext } from "../../../../../context/currentTripContext/currentTripContextInterface";
import { StateContext } from "../../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../../context/globalContext/interface";
import { useRecommendedTripContext } from "../../../../../context/recommendedTripContext/recommendedTripContext";
import { iRecommendedTripContext } from "../../../../../context/recommendedTripContext/recommendedTripContextInterface";
import { iSocketContext, useSocketContext } from "../../../../../context/socketContext/SocketContext";
import { ScreenTypes } from "../../../../../enums";
import { toastMessage } from "../../../../../helpers/toast/toastMessage";
import useCustomErrorHandler from "../../../../../hooks/useCustomErrorHandler";
import { GetItinerariesListResponse } from "../../../../../interface";
import { Itineraries } from "../../../../../types";
import { sortDatesInAscendingOrder } from "../../../../../utils/dateUtils";
import { useResetState } from "../../../../../utils/resetBookingData";
import { clearChatBookingOrigin } from "../../../../../utils/booking/clearChatBookingOrigin";
import { rotateChatId } from "../../../../../utils/chat/rotateChatId";
import { desktopSidebarTabs } from "../desktopTabs";
import { usePixelEvent } from "../../../../../hooks/usePixelEvent";
import { useNavigationUrl } from "../../../../../hooks/useNavigationUrl";

interface UseTabsLogicProps {
  setIsOpenMobileSidebar?: Dispatch<SetStateAction<boolean>>;
}

export const useTabsLogic = ({ setIsOpenMobileSidebar }: UseTabsLogicProps) => {
  const [value, setValue] = useState<number>(1);
  const [tripCount, setTripCount] = useState<number>(0);
  const { navigateWithParams } = useNavigationUrl();
  const [listItinery, setListItinery] = useState<Itineraries | null>(null);
  const { sendPixelEvent } = usePixelEvent();

  const globalContext = useContext(StateContext);
  const {
    setChatInputText,
    appConfig,
    isMobile,
    setScreen,
    userInfo,
    setTrigerredListItineraryAPIFlag,
    setFirstScreen,
    firstScreen,
    setMyTripActiveTab,
    accessJWT,
    setChatData,
    setLoginCreateAccountState,
    setShowZoho,
    setActiveBreadCrumbs,
    setHideDetails,
    setRecentTravelersList,
    setHotelTravelersList,
    setGuestList,
    setAirPorts,
    setFlightData,
    setHotelData,
    setSelectedFlightOptions,
    setBookingDetails,
    setSelectedHotelRoomArr,
    setSelectedChatData,
    setBookClickBeforeLoginFlag,
  }: iGlobalContext = globalContext;

  const { handleCustomError } = useCustomErrorHandler();
  const { currentTripData }: iCurrentTripContext = useCurrentTripContext();
  const { handleCloseSocketConnection, setStreamingFlagAndResponseType }: iSocketContext = useSocketContext();
  const { setDisableInput, handleCancel }: iRecommendedTripContext = useRecommendedTripContext();
  const [showGetHelpOptions, setShowGetHelpOptions] = useState<boolean>(false);
  const resetState = useResetState();
  const resetBookingValues = useResetState();

  /**
   * Handling tabs and switching between tabs
   */
  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleNav = () => {
    triggerGAEvent(GA_LABEL_ENUMS.new_trip_click);
    setChatData([]);
    setDisableInput(false);
    handleCloseSocketConnection();
    handleCancel();
    setChatInputText("");
    // Fresh conversation → fresh chat id so the backend loads an empty
    // TripContext (else a bare "Hi" re-runs the previous chat's search).
    rotateChatId();
    setSelectedChatData({ ...SELECTED_CHAT_DATA });
    navigateWithParams(ROUTES.Home);
    setScreen("");
    setFirstScreen(ScreenTypes.NewTrip);
    resetBookingValues();
  };

  useEffect(() => {
    setValue(0);
    setTimeout(() => {
      if (isMobile) setValue(0);
      else {
        const selectedScreenIndexValue = desktopSidebarTabs.findIndex(
          (sideNavItem) => sideNavItem.enumType === firstScreen
        );
        if (selectedScreenIndexValue >= 0) {
          setValue(selectedScreenIndexValue);
          return;
        }
        setValue(desktopSidebarTabs.length);
      }
    }, 500);
  }, [firstScreen, isMobile]);

  const handleLastTab = () => {
    triggerGAEvent(GA_LABEL_ENUMS.new_trip_click);
    setChatInputText("");
    if (setIsOpenMobileSidebar) setIsOpenMobileSidebar(false);
    navigateWithParams(ROUTES.NewTrip);
    setValue(value);
  };

  /**
   * Save trip login
   */
  const [isLoadingItineraryList, setIsLoadingItineraryList] = useState(false);
  async function getListItineries() {
    try {
      setIsLoadingItineraryList(true);
      const response: GetItinerariesListResponse = await getItinerariesListAPI(accessJWT, userInfo?.id, "saved", 0, 10);
      if (response?.statusCode !== 200) {
        handleCustomError(response?.statusCode, "itinerary.list");
        return null;
      }
      setIsLoadingItineraryList(false);
      if (response && response?.data) {
        setListItinery(sortDatesInAscendingOrder(response?.data, "createdAt"));
        setTripCount(response?.data.length);
      } else {
        throw new Error("No response data");
      }
    } catch (error) {
      toastMessage.error("OOPS", "Something went wrong!");
    }
    setTrigerredListItineraryAPIFlag(false);
  }

  const handleBook = () => {
    // Leaving the chat funnel for a fresh Shop Travel (chatless) search — drop the
    // chat-origin markers so search/room-pricing/availability/review go out as v1.
    clearChatBookingOrigin();
    setStreamingFlagAndResponseType({
      chatId: "",
      isStillStreamingFlag: false,
      responseType: "",
      showMoreClickFlag: false,
    });
    setRecentTravelersList([]);
    setHotelTravelersList([]);
    setGuestList([]);
    setAirPorts([]);
    setFlightData(null);
    setHotelData(null);
    setSelectedFlightOptions({
      flights: true,
      hotels: false,
      activities: false,
    });
    setBookingDetails((prevData) => ({
      ...prevData,
      selectedFlightId: "",
      flightPreferences: {
        connections: null,
        airlines: [],
        refundable: null,
        departureTime: [],
      },
      hotelPreferences: {
        refundable: null,
        hotelChains: [],
        accommodationTypes: [""],
        userRatings: null,
        starRatings: [],
      },
      roomInfo: [
        {
          roomNo: 1,
          adults: 2,
          child: 0,
          childAges: "",
        },
      ],
    }));
    setSelectedHotelRoomArr((prev) =>
      prev.map((hotel) => ({
        ...hotel,
        selectedHotelRoom: null,
      }))
    );
    setSelectedChatData(SELECTED_CHAT_DATA);
    if (isMobile) {
      resetState();
      // Clear stale firstScreen so a mobile→desktop resize renders the centered
      // Shop-Travel layout (gated on screen===newBooking && firstScreen===""). Mirrors
      // the desktop branch; no mobile effect (firstScreen drives the desktop layout only).
      // Gated by the middle-panel appConfig flag so MIDDLE_PANEL_ON absent/false matches the old method.
      if (resolveBoolFlag(appConfig, APP_CONFIG.MIDDLE_PANEL_ON)) {
        setFirstScreen("");
      }
      navigateWithParams(ROUTES.BookingInProgress);
    } else {
      setScreen(ScreenTypes.newBooking);
      resetState();
      setSelectedChatData(SELECTED_CHAT_DATA);
      setFirstScreen("");
    }
    setBookClickBeforeLoginFlag(true);
  };

  return {
    value,
    setValue,
    tripCount,
    setTripCount,
    listItinery,
    setListItinery,
    isLoadingItineraryList,
    setIsLoadingItineraryList,
    handleChange,
    handleNav,
    handleLastTab,
    getListItineries,
    handleBook,
    showGetHelpOptions,
    setShowGetHelpOptions,
    userInfo,
    isMobile,
    currentTripData,
    setScreen,
    setMyTripActiveTab,
    dataProps: {
      setChatInputText,
      setFirstScreen,
      setActiveBreadCrumbs,
      setHotelData,
      setFlightData,
      setHideDetails,
      resetBookingValues,
      sendPixelEvent,
      setLoginCreateAccountState,
      setShowZoho,
    },
  };
};
